"use client";

import React from "react";
import { Box, Typography } from "@mui/material";
import Image from "next/image";
import { ColletteButton } from "@collette-travel/collette-common-controls";
import { TravelStyleCard as TravelStyleCardType } from "@/types/dto/deals-page/deals-page-types";
import sanitizeHtml from "@/utils/sanitize-html";
import Link from "next/link";

interface TravelStyleCardProps {
  card: TravelStyleCardType;
  cardIndex?: number;
}

const TravelStyleCard: React.FC<TravelStyleCardProps> = ({ card, cardIndex }) => {
  const cardId = cardIndex !== undefined ? `travel-style-card-${cardIndex}` : "travel-style-card";

  const { ctaUrl, ctaText } = card;

  const hasCta = ctaText && ctaUrl;

  return (
    <Box
      data-testid={cardId}
      data-card-name={card.title}
      data-carousel-position={cardIndex !== undefined ? String(cardIndex + 1) : undefined}
      sx={{
        width: "100%",
        maxWidth: { xs: "100%", sm: "400px" },
        boxShadow: 6,
        borderRadius: "20px",
        overflow: "hidden",
        backgroundColor: "background.default",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        cursor: "pointer",
        textDecoration: "none",
        "&:hover .cta-find-deals-button": {
          backgroundColor: "background.cool_strong"
        }
      }}
    >
      {hasCta && (
        <Link
          data-testid="travel-style-card-link"
          href={ctaUrl}
          tabIndex={-1}
          style={{
            zIndex: 5,
            alignItems: "center",
            display: "flex"
          }}
          className="pseudo-absolute-full-container"
        />
      )}
      {/* Badge - positioned within image area */}
      {card.badge && (
        <Box
          data-testid="travel-style-card-badge"
          sx={{
            position: "absolute",
            top: 14,
            right: 14,
            backgroundColor: "accent.gold",
            color: "text.primary",
            padding: "6px",
            borderRadius: "10px",
            zIndex: 2,
            fontFamily: "Montserrat, sans-serif",
            fontWeight: 600,
            fontSize: "12px",
            letterSpacing: "0.4px",
            lineHeight: 1.5
          }}
        >
          {card.badge}
        </Box>
      )}

      {/* Image Area - only top corners rounded */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: { xs: "180px", sm: "218px" },
          overflow: "hidden",
          borderTopLeftRadius: "20px",
          borderTopRightRadius: "20px"
        }}
      >
        {card.image.imageUrl && (
          <Image
            src={card.image.imageUrl}
            alt={card.image.altText || card.title}
            fill
            style={{ objectFit: "cover" }}
            data-testid={`${cardId}-image`}
          />
        )}
      </Box>

      {/* Content Area - border only here, bottom corners rounded */}
      <Box
        sx={{
          padding: "20px",
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          border: "1px solid",
          borderColor: "divider",
          borderTop: "none",
          borderBottomLeftRadius: "20px",
          borderBottomRightRadius: "20px",
          backgroundColor: "background.default",
          flexGrow: 1
        }}
      >
        <Typography
          variant="h5"
          component="h3"
          sx={{
            fontFamily: "Montserrat, sans-serif",
            fontWeight: 600,
            fontSize: "20px",
            lineHeight: "normal",
            color: "text.primary",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden"
          }}
          data-testid={`${cardId}-title`}
        >
          {card.title}
        </Typography>

        {card.description && (
          <Typography
            variant="body1"
            component="div"
            sx={{
              fontFamily: "Montserrat, sans-serif",
              fontWeight: 500,
              fontSize: "18px",
              lineHeight: 1.5,
              color: "text.primary",
              "& p": { margin: 0 }
            }}
            data-testid={`${cardId}-description`}
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(card.description) }}
          />
        )}

        {hasCta && (
          <Box sx={{ mt: "auto", pt: 1, display: "flex", justifyContent: "center" }}>
            <ColletteButton
              component="a"
              href={card.ctaUrl}
              label={card.ctaText}
              testId={`${cardId}-cta`}
              variant="outlined"
              size="large"
              className="cta-find-deals-button"
              sx={{
                zIndex: 8
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default TravelStyleCard;
